import React, { useState } from 'react';
import {
  HelpCircle,
  Search,
  Book,
  MessageCircle,
  Video,
  Mail,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  Play
} from 'lucide-react';

const faqs = [
  {
    question: 'How do I create my first website?',
    answer: 'Click on "Create Website" from the dashboard or go to My Websites and click the "Create Website" button. Follow the 8-step wizard to set up your site.'
  },
  {
    question: 'Can I use my own domain?',
    answer: 'Yes! You can connect your custom domain on Starter plan and above. Go to your website settings and follow the domain connection instructions.'
  },
  {
    question: 'How do I add products to my store?',
    answer: 'Navigate to the Products section from the sidebar, click "Add Product", and fill in the product details including name, price, description, and images.'
  },
  {
    question: 'What payment methods are supported?',
    answer: 'We support Stripe, PayPal, Razorpay, bKash, Nagad, and Upay. You can configure these in your website settings.'
  },
  {
    question: 'How do I generate QR codes for my website?',
    answer: 'Go to the User Kit section, select your website, customize the QR code appearance, and download it in your preferred format.'
  },
  {
    question: 'Can I invite team members?',
    answer: 'Team collaboration is available on Enterprise plan. Go to Team section to invite members and assign roles.'
  }
];

const resources = [
  {
    title: 'Documentation',
    description: 'Comprehensive guides and API references',
    icon: Book,
    link: '#',
    color: 'bg-blue-500'
  },
  {
    title: 'Video Tutorials',
    description: 'Step-by-step video guides',
    icon: Video,
    link: '#',
    color: 'bg-red-500'
  },
  {
    title: 'Community Forum',
    description: 'Connect with other users',
    icon: MessageCircle,
    link: '#',
    color: 'bg-green-500'
  },
  {
    title: 'Blog',
    description: 'Latest updates and tips',
    icon: FileText,
    link: '#',
    color: 'bg-purple-500'
  }
];

const Help = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">How can we help?</h1>
        <p className="text-gray-500">Search our knowledge base or browse categories below</p>
      </div>

      {/* Search */}
      <div className="max-w-2xl mx-auto">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
          <input
            type="text"
            placeholder="Search for answers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-14 pr-4 py-4 text-lg border border-gray-300 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-sm"
          />
        </div>
      </div>

      {/* Resources */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {resources.map((resource, index) => (
          <a
            key={index}
            href={resource.link}
            className="flex items-start gap-4 p-6 bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
          >
            <div className={`w-12 h-12 ${resource.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
              <resource.icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{resource.title}</h3>
              <p className="text-sm text-gray-500 mt-1">{resource.description}</p>
            </div>
          </a>
        ))}
      </div>

      {/* FAQ Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Frequently Asked Questions</h2>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq, index) => (
              <div key={index} className="p-6">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <span className="font-medium text-gray-900">{faq.question}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === index && (
                  <p className="mt-3 text-gray-600">{faq.answer}</p>
                )}
              </div>
            ))
          ) : (
            <div className="p-8 text-center">
              <p className="text-gray-500">No results found for "{searchQuery}"</p>
              <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
            </div>
          )}
        </div>
      </div>

      {/* Contact Support */}
      <div className="bg-gradient-to-r from-primary-500 to-purple-600 rounded-2xl p-8 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Still need help?</h2>
            <p className="text-primary-100">
              Our support team is available 24/7 to assist you
            </p>
          </div>
          <div className="flex gap-3">
            <a
              href="mailto:support@hostbit.cloud"
              className="flex items-center gap-2 px-6 py-3 bg-white text-primary-600 rounded-xl font-medium hover:bg-gray-100 transition-colors"
            >
              <Mail className="w-5 h-5" />
              Email Support
            </a>
            <button
              onClick={() => {/* Live chat */}}
              className="flex items-center gap-2 px-6 py-3 bg-white/20 text-white rounded-xl font-medium hover:bg-white/30 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              Live Chat
            </button>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Getting Started</h3>
          <ul className="space-y-3">
            <li>
              <a href="#" className="text-primary-600 hover:text-primary-700 flex items-center gap-2">
                <Play className="w-4 h-4" />
                Quick Start Guide
              </a>
            </li>
            <li>
              <a href="#" className="text-primary-600 hover:text-primary-700 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Creating Your First Site
              </a>
            </li>
            <li>
              <a href="#" className="text-primary-600 hover:text-primary-700 flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                Connecting Your Domain
              </a>
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Account & Billing</h3>
          <ul className="space-y-3">
            <li>
              <a href="#" className="text-primary-600 hover:text-primary-700 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Manage Subscription
              </a>
            </li>
            <li>
              <a href="#" className="text-primary-600 hover:text-primary-700 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Update Payment Method
              </a>
            </li>
            <li>
              <a href="#" className="text-primary-600 hover:text-primary-700 flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                View Invoices
              </a>
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Advanced Features</h3>
          <ul className="space-y-3">
            <li>
              <a href="#" className="text-primary-600 hover:text-primary-700 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Using the Visual Editor
              </a>
            </li>
            <li>
              <a href="#" className="text-primary-600 hover:text-primary-700 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Setting Up E-commerce
              </a>
            </li>
            <li>
              <a href="#" className="text-primary-600 hover:text-primary-700 flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                API Documentation
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Help;
